import { TConfiguration } from '../../../services/central-configuration/CentralConfigurationService';
import { TenantExpirationDetails } from '../../../services/TenantsService';
import { ProductType } from '../../../types/Tenant';

type TPlatformFeature = {
	label: string;
	productType?: ProductType;
	toggleKey?: string;
};

type TPlatformPageConfig = {
	url?: string;
	productType: ProductType;
};

type TPlatformCardData = {
	description: string;
	enabledPlatformFeatures: string[];
	expirationDetails?: TenantExpirationDetails;
	redirectionUrl?: string;
};

const PLATFORM_FEATURES: TPlatformFeature[] = [
	{
		label: 'Upload WFA sessions',
		productType: ProductType.WEB_FLOW_ANALYZER
	},
	{
		label: 'Upload WSDKs tests',
		productType: ProductType.WEB_SDK
	},
	{
		label: 'Upload MFA sessions',
		productType: ProductType.MOBILE_FLOW_ANALYZER
	},
	{
		label: 'Upload MSDKs tests',
		productType: ProductType.MOBILE_SDK
	},
	{
		label: 'Accessibility dashboard',
		toggleKey: 'ENABLE_NEW_PLATFORM_DASHBOARD'
	}
];

const getPlatformPageRedirectionOrder = (): TPlatformPageConfig[] => {
	return [
		{
			url: import.meta.env.VITE_PLATFORM_DASHBOARD_URL,
			productType: ProductType.PLATFORM
		},
		{
			url: import.meta.env.VITE_MOBILE_SDK_DASHBOARD_URL,
			productType: ProductType.MOBILE_SDK
		},
		{
			url: import.meta.env.VITE_WEB_SDK,
			productType: ProductType.WEB_SDK
		},
		{
			url: import.meta.env.VITE_MFA_DASHBOARD_URL,
			productType: ProductType.MOBILE_FLOW_ANALYZER
		},
		{
			url: import.meta.env.VITE_WFA_DASHBOARD_URL,
			productType: ProductType.WEB_FLOW_ANALYZER
		}
	];
};

export const getProductExpirationDetails = (
	productType: ProductType | undefined,
	productExpirationDetails?: Map<ProductType, TenantExpirationDetails>
): TenantExpirationDetails | undefined => {
	return productType ? productExpirationDetails?.get(productType) : undefined;
};

export const hasProductLicense = (
	productType: ProductType | undefined,
	productExpirationDetails?: Map<ProductType, TenantExpirationDetails>
): boolean => !!getProductExpirationDetails(productType, productExpirationDetails);

const getConfigToggleValue = (
	toggleKey: string | undefined,
	configuration: TConfiguration
): boolean => {
	if (!toggleKey) {
		return false;
	}
	return !!configuration?.[toggleKey];
};

export const getEnabledPlatformFeatures = (
	configuration: TConfiguration,
	productExpirationDetails?: Map<ProductType, TenantExpirationDetails>
): string[] => {
	return PLATFORM_FEATURES.filter(
		({ productType, toggleKey }) =>
			getConfigToggleValue(toggleKey, configuration) ||
			hasProductLicense(productType, productExpirationDetails)
	).map(({ label }) => label);
};

export const getFirstEnabledPlatformPage = (
	configuration: TConfiguration,
	productExpirationDetails?: Map<ProductType, TenantExpirationDetails>
): TPlatformPageConfig | undefined => {
	return getPlatformPageRedirectionOrder().find(
		(page) =>
			!!page.url &&
			(page.productType === ProductType.PLATFORM
				? getConfigToggleValue('ENABLE_NEW_PLATFORM_DASHBOARD', configuration)
				: hasProductLicense(page.productType, productExpirationDetails))
	);
};

export const getFallbackPlatformPage = (): TPlatformPageConfig | undefined => {
	return getPlatformPageRedirectionOrder().find((page) => !!page.url);
};

export const getPlatformCardDescription = (enabledPlatformFeatures: string[]): string => {
	return enabledPlatformFeatures.length
		? `${enabledPlatformFeatures.join(', ')}.`
		: 'Platform features are enabled for your tenant.';
};

export const getPlatformCardData = (
	configuration: TConfiguration,
	productExpirationDetails?: Map<ProductType, TenantExpirationDetails>
): TPlatformCardData | null => {
	const enabledPlatformFeatures = getEnabledPlatformFeatures(
		configuration,
		productExpirationDetails
	);
	const firstEnabledPlatformPage = getFirstEnabledPlatformPage(
		configuration,
		productExpirationDetails
	);

	if (!enabledPlatformFeatures.length && !firstEnabledPlatformPage) {
		return null;
	}

	const fallbackPage = getFallbackPlatformPage();
	const redirectionUrl = firstEnabledPlatformPage?.url || fallbackPage?.url;
	const expirationDetails =
		getProductExpirationDetails(ProductType.PLATFORM, productExpirationDetails) ||
		getProductExpirationDetails(firstEnabledPlatformPage?.productType, productExpirationDetails) ||
		getProductExpirationDetails(fallbackPage?.productType, productExpirationDetails);

	return {
		description: getPlatformCardDescription(enabledPlatformFeatures),
		enabledPlatformFeatures,
		expirationDetails,
		redirectionUrl
	};
};
